import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { lazyRetry as lazy } from './lazyRetry';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import NotFoundPage from '../pages/NotFoundPage';
import RequirePermission from './RequirePermission';
import { PERMISSIONS } from '../security/acl';
import AdsDashboardPage from '../features/ads/AdsDashboardPage.jsx';
import AdsAdvertisersPage from '../features/ads/AdsAdvertisersPage.jsx';
import AdsCampaignsListPage from '../features/ads/AdsCampaignsList.jsx';
import AdsCampaignEditorPage from '../features/ads/AdsCampaignEditorPage.jsx';
import AdsPlacementsPage from '../features/ads/AdsPlacementsPage.jsx';
import AdsLandingPagesPage from '../features/ads/AdsLandingPagesPage.jsx';
import AdsReportsPage from '../features/ads/AdsReportsPage.jsx';
import AdsSettingsPage from '../features/ads/AdsSettingsPage.jsx';

const Dashboard = lazy(() => import('../features/dashboard/Dashboard'));
const WatchtowerPage = lazy(() => import('../features/watchtower/WatchtowerPage'));
const BookingsPage = lazy(() => import('../features/bookings/BookingsPage'));
const BookingViewPage = lazy(() => import('../features/bookings/BookingViewPage'));
const BookingEditPage = lazy(() => import('../features/bookings/BookingEditPage'));
const BookingActivityLogsPage = lazy(() => import('../features/bookings/BookingActivityLogsPage'));
const BookingInvoicesPage = lazy(() => import('../features/bookings/BookingInvoicesPage'));
const BookingTransactionsPage = lazy(() => import('../features/bookings/BookingTransactionsPage'));
const ManualBroadcastPage = lazy(() => import('../features/bookings/ManualBroadcastPage'));
const GuestVendorInterestsPage = lazy(() => import('../features/bookings/GuestVendorInterestsPage'));
const VendorCabAvailabilityPage = lazy(() => import('../features/bookings/VendorCabAvailabilityPage'));
const LeadsPage = lazy(() => import('../features/bookings/LeadsPage'));
const QuotesPage = lazy(() => import('../features/bookings/QuotesPage'));
const QuoteViewPage = lazy(() => import('../features/bookings/QuoteViewPage'));
const VendorsPage = lazy(() => import('../features/vendors/VendorsPage'));
const VendorJoinUsEnquiriesPage = lazy(() => import('../features/vendors/VendorJoinUsEnquiriesPage'));
const PanIndiaVendorsPage = lazy(() => import('../features/vendors/PanIndiaVendorsPage'));
const DmcCabProspectsPage = lazy(() => import('../features/dmc/DmcCabProspectsPage'));
const DmcPackageProspectsPage = lazy(() => import('../features/dmc/DmcPackageProspectsPage'));
const VendorOnboardingCampaignsPage = lazy(() => import('../features/vendors/VendorOnboardingCampaignsPage'));
const VendorOnboardingFormSettingsPage = lazy(() => import('../features/vendors/VendorOnboardingFormSettingsPage'));
const CabsManagementPage = lazy(() => import('../features/cabs/CabsManagementPage'));
const CabsApprovalPage = lazy(() => import('../features/cabs/CabsApprovalPage'));
const CabExpensesPage = lazy(() => import('../features/cabs/CabExpensesPage'));
const CabWiseRevenuePage = lazy(() => import('../features/cabs/CabWiseRevenuePage'));
const CabSoldOutPage = lazy(() => import('../features/cabs/CabSoldOutPage'));
const DriverManagementPage = lazy(() => import('../features/drivers/DriverManagementPage'));
const DriverApprovalPage = lazy(() => import('../features/drivers/DriverApprovalPage'));
const DriverSalarySlipPage = lazy(() => import('../features/drivers/DriverSalarySlipPage'));
const DriverExpensesPage = lazy(() => import('../features/drivers/DriverExpensesPage'));
const CabPackagesPage = lazy(() => import('../features/cab-packages/CabPackagesPage'));
const CabPackageEnquiriesPage = lazy(() => import('../features/cab-packages/CabPackageEnquiriesPage'));
const CabPackageUserLoggedPage = lazy(() => import('../features/cab-packages/CabPackageUserLoggedPage'));
const RoutePageUserLoggedPage = lazy(() => import('../features/routes/RoutePageUserLoggedPage'));
const VehicleLandingUserLoggedPage = lazy(() => import('../features/routes/VehicleLandingUserLoggedPage'));
const ContactMessagesPage = lazy(() => import('../features/contact/ContactMessagesPage'));
const ContactFormSettingsPage = lazy(() => import('../features/contact/ContactFormSettingsPage'));
const NearbyPlacesPage = lazy(() => import('../features/nearby-places/NearbyPlacesPage'));
const PilgrimageSitesPage = lazy(() => import('../features/jyotirlinga/PilgrimageSitesPage'));
const JyotirlingaEnginePage = lazy(() => import('../features/jyotirlinga/JyotirlingaEnginePage'));
const AshtavinayakToursPage = lazy(() => import('../features/ashtavinayak-cms/AshtavinayakToursPage'));
const AshtavinayakTourEditPage = lazy(() => import('../features/ashtavinayak-cms/AshtavinayakTourEditPage'));
const AshtavinayakTemplesPage = lazy(() => import('../features/ashtavinayak-cms/AshtavinayakTemplesPage'));
const LuxuryCarsDashboardPage = lazy(() => import('../features/luxury-cars/LuxuryCarsDashboardPage'));
const LuxuryCitiesPage = lazy(() => import('../features/luxury-cars/LuxuryCitiesPage'));
const LuxuryCarsFleetPage = lazy(() => import('../features/luxury-cars/LuxuryCarsFleetPage'));
const LuxuryTariffsPage = lazy(() => import('../features/luxury-cars/LuxuryTariffsPage'));
const LuxuryPackagesPage = lazy(() => import('../features/luxury-cars/LuxuryPackagesPage'));
const LuxuryPagesPage = lazy(() => import('../features/luxury-cars/LuxuryPagesPage'));
const LuxuryWelcomeGoodiesPage = lazy(() => import('../features/luxury-cars/LuxuryWelcomeGoodiesPage'));
const LuxurySettingsPage = lazy(() => import('../features/luxury-cars/LuxurySettingsPage'));
const LuxuryTermsPage = lazy(() => import('../features/luxury-cars/LuxuryTermsPage'));
const LuxuryEnquiriesPage = lazy(() => import('../features/luxury-cars/LuxuryEnquiriesPage'));
const LuxuryBookingsPage = lazy(() => import('../features/luxury-cars/LuxuryBookingsPage'));
const LuxuryVendorsPage = lazy(() => import('../features/luxury-cars/LuxuryVendorsPage'));
const LuxuryVendorEditPage = lazy(() => import('../features/luxury-cars/LuxuryVendorEditPage'));
const LuxuryPricingMatrixPage = lazy(() => import('../features/luxury-cars/LuxuryPricingMatrixPage'));
const LuxuryCarsActivitiesPage = lazy(() => import('../features/luxury-cars/LuxuryCarsActivitiesPage'));
const LuxuryFaqsPage = lazy(() => import('../features/luxury-cars/LuxuryFaqsPage'));
const LuxuryFaqCategoriesPage = lazy(() => import('../features/luxury-cars/LuxuryFaqCategoriesPage'));
const LuxuryFaqImportPage = lazy(() => import('../features/luxury-cars/LuxuryFaqImportPage'));
const LuxuryFaqTemplatesPage = lazy(() => import('../features/luxury-cars/LuxuryFaqTemplatesPage'));
const LuxuryFaqAnalyticsPage = lazy(() => import('../features/luxury-cars/LuxuryFaqAnalyticsPage'));
const ToursPackagesSettingsPage = lazy(() => import('../features/tours/ToursPackagesSettingsPage'));
const BusinessPresenceDashboard = lazy(
  () => import('../features/business-presence/BusinessPresenceDashboard'),
);
const BusinessLocationsPage = lazy(() => import('../features/business-presence/BusinessLocationsPage'));
const BusinessPostsPage = lazy(() => import('../features/business-presence/BusinessPostsPage'));
const BusinessReviewsInboxPage = lazy(
  () => import('../features/business-presence/BusinessReviewsInboxPage'),
);
const BusinessPresenceSettingsPage = lazy(
  () => import('../features/business-presence/BusinessPresenceSettingsPage'),
);
const BusinessSocialAccountsPage = lazy(
  () => import('../features/business-presence/BusinessSocialAccountsPage'),
);
const BusinessSeoPagesPage = lazy(() => import('../features/business-presence/BusinessSeoPagesPage'));
const BusinessPresenceOAuthCallbackPage = lazy(
  () => import('../features/business-presence/BusinessPresenceOAuthCallbackPage'),
);
const PricingPage = lazy(() => import('../features/pricing/PricingPage'));
const VendorPricingPage = lazy(() => import('../features/pricing/VendorPricingPage'));
const PricingTestPage = lazy(() => import('../features/pricing/PricingTestPage'));
const PriceAnalysisPage = lazy(() => import('../features/pricing/price-analysis/PriceAnalysisPage'));
const PriceAnalysisHistoryPage = lazy(() => import('../features/pricing/price-analysis/PriceAnalysisHistoryPage'));
const PricingConfigurationPage = lazy(() => import('../features/pricing/price-analysis/PricingConfigurationPage'));
const CodeFlowPage = lazy(() => import('../features/code-flow/CodeFlowPage'));
const DevDocPage = lazy(() => import('../features/dev-doc/DevDocPage'));
const DbBackupPage = lazy(() => import('../features/tools/DbBackupPage'));
const CacheManagementPage = lazy(() => import('../features/tools/CacheManagementPage'));
const TariffConfigPage = lazy(() => import('../features/routes/TariffConfigPage'));
const ConfigRouteEditPage = lazy(() => import('../features/routes/ConfigRouteEditPage'));
const RoutesSettingsPage = lazy(() => import('../features/routes/RoutesSettingsPage'));
const DistanceCalculatorPage = lazy(() => import('../features/distance/DistanceCalculatorPage'));
const CityManagementPage = lazy(() => import('../features/cities/CityManagementPage'));
const LocationMissingSearchesPage = lazy(() => import('../features/cities/LocationMissingSearchesPage'));
const LocationSearchIndexPage = lazy(() => import('../features/cities/LocationSearchIndexPage'));
const LocationMasterOpsPage = lazy(() => import('../features/cities/LocationMasterOpsPage'));
const RoutePagesManagement = lazy(() => import('../features/routes/RoutePagesManagement'));
const SeoControlCenterPage = lazy(() => import('../features/seo/SeoControlCenterPage'));
const SeoSetupWizardPage = lazy(() => import('../features/seo/SeoSetupWizardPage'));
const SeoAnalyzerPage = lazy(() => import('../features/seo/SeoAnalyzerPage'));
const SeoSerpPreviewPage = lazy(() => import('../features/seo/SeoSerpPreviewPage'));
const SeoSchemaPage = lazy(() => import('../features/seo/SeoSchemaPage'));
const SeoRankTrackerPage = lazy(() => import('../features/seo/SeoRankTrackerPage'));
const Seo404MonitorPage = lazy(() => import('../features/seo/Seo404MonitorPage'));
const SeoContentOverviewPage = lazy(() => import('../features/seo/SeoContentOverviewPage'));
const SeoInternalLinksPage = lazy(() => import('../features/seo/SeoInternalLinksPage'));
const SeoImageSeoPage = lazy(() => import('../features/seo/SeoImageSeoPage'));
const SeoPageUrlsPage = lazy(() => import('../features/seo/SeoPageUrlsPage'));
const SeoScoreDashboardPage = lazy(() => import('../features/seo/SeoScoreDashboardPage'));
const LocalityLandingCmsPage = lazy(() => import('../features/seo/LocalityLandingCmsPage'));
const CityMoneyPagesPage = lazy(() => import('../features/seo/CityMoneyPagesPage'));
const SeoEngineNestedRoutes = lazy(() => import('../features/seo-engine/SeoEngineNestedRoutes'));
const AiSeoNestedRoutes = lazy(() => import('../features/ai-seo/AiSeoNestedRoutes'));
const SeoLocalSeoPage = lazy(() => import('../features/seo/SeoLocalSeoPage'));
const SeoIndexStatusPage = lazy(() => import('../features/seo/SeoIndexStatusPage'));
const SitemapPage = lazy(() => import('../features/marketing/SitemapPage'));
const RobotsTxtPage = lazy(() => import('../features/marketing/RobotsTxtPage'));
const CmsRoutePagesManagement = lazy(() => import('../features/routes/CmsRoutePagesManagement'));
const CmsRoutePageEditor = lazy(() => import('../features/routes/CmsRoutePageEditor'));
const CmsRoutePageCssEditor = lazy(() => import('../features/routes/CmsRoutePageCssEditor'));
const HomepagePopularRoutesPage = lazy(() => import('../features/content/HomepagePopularRoutesPage'));
const HomepagePopularRouteCategoriesPage = lazy(
  () => import('../features/content/HomepagePopularRouteCategoriesPage')
);
const HomepageHeaderPage = lazy(() => import('../features/homepage/HomepageHeaderPage'));
const HomepageNotFoundPage = lazy(() => import('../features/homepage/HomepageNotFoundPage'));
const HomepageHeroBannerPage = lazy(() => import('../features/homepage/HomepageHeroBannerPage'));
const HomepageFooterPage = lazy(() => import('../features/homepage/HomepageFooterPage'));
const HomepageSectionsPage = lazy(() => import('../features/homepage/HomepageSectionsPage'));
const GalleryShareSettingsPage = lazy(() => import('../features/homepage/GalleryShareSettingsPage'));
const FaqCategoriesPage = lazy(() => import('../features/content/FaqCategoriesPage'));
const FaqsPage = lazy(() => import('../features/content/FaqsPage'));
const BlogPostsManagement = lazy(() => import('../features/content/BlogPostsManagement'));
const BlogPostEditor = lazy(() => import('../features/content/BlogPostEditor'));
const BlogCategoriesPage = lazy(() => import('../features/content/BlogCategoriesPage'));
const BlogTagsPage = lazy(() => import('../features/content/BlogTagsPage'));
const BlogCommentsPage = lazy(() => import('../features/content/BlogCommentsPage'));
const BlogSeoDashboard = lazy(() => import('../features/content/BlogSeoDashboard'));
const BlogKeywordsPage = lazy(() => import('../features/content/BlogKeywordsPage'));
const BlogClustersPage = lazy(() => import('../features/content/BlogClustersPage'));
const BlogCalendarPage = lazy(() => import('../features/content/BlogCalendarPage'));
const BlogSeoAuditPage = lazy(() => import('../features/content/BlogSeoAuditPage'));
const BlogCannibalizationPage = lazy(() => import('../features/content/BlogCannibalizationPage'));
const BlogRedirectsPage = lazy(() => import('../features/content/BlogRedirectsPage'));
const BlogInternalLinksPage = lazy(() => import('../features/content/BlogInternalLinksPage'));
const BlogAuthorsPage = lazy(() => import('../features/content/BlogAuthorsPage'));
const ReviewsPage = lazy(() => import('../features/content/ReviewsPage'));
const SegmentConfigurationPage = lazy(() => import('../features/segments/SegmentConfigurationPage'));
const CorporateClientsPage = lazy(() => import('../features/corporate/CorporateClientsPage'));
const CorporatePendingApprovalsPage = lazy(() => import('../features/corporate/CorporatePendingApprovalsPage'));
const CorporateClientDetailPage = lazy(() => import('../features/corporate/CorporateClientDetailPage'));
const CorporateBookingsPage = lazy(() => import('../features/corporate/CorporateBookingsPage'));
const CorporatePricingPage = lazy(() => import('../features/corporate/CorporatePricingPage'));
const CorporateInvoicesAdminPage = lazy(() => import('../features/corporate/CorporateInvoicesAdminPage'));
const CorporateReportsPage = lazy(() => import('../features/corporate/CorporateReportsPage'));
const CorporateContractsPage = lazy(() => import('../features/corporate/CorporateContractsPage'));
const CorporateErpPage = lazy(() => import('../features/corporate/CorporateErpPage'));
const CorporateCmsHubPage = lazy(() => import('../features/corporate-cms/CorporateCmsHubPage'));
const CorporateCmsCitiesPage = lazy(() => import('../features/corporate-cms/CorporateCmsCitiesPage'));
const CorporateCmsServicesPage = lazy(() => import('../features/corporate-cms/CorporateCmsServicesPage'));
const CorporateCmsPackagesPage = lazy(() => import('../features/corporate-cms/CorporateCmsPackagesPage'));
const CorporateCmsEnquiriesPage = lazy(() => import('../features/corporate-cms/CorporateCmsEnquiriesPage'));
const CorporateCmsLocalitiesPage = lazy(() => import('../features/corporate-cms/CorporateCmsLocalitiesPage'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const CustomerActivitiesLogPage = lazy(() => import('../features/users/CustomerActivitiesLogPage'));
const CustomerViewPage = lazy(() => import('../features/users/CustomerViewPage'));
const CustomerEditPage = lazy(() => import('../features/users/CustomerEditPage'));
const AnalyticsPage = lazy(() => import('../features/analytics/AnalyticsPage'));
const AdvancedAnalyticsPage = lazy(() => import('../features/analytics/AdvancedAnalyticsPage'));
const BookingFunnelPage = lazy(() => import('../features/analytics/BookingFunnelPage'));
const VisitorsReportPage = lazy(() => import('../features/analytics/VisitorsReportPage'));
const CancellationReportsPage = lazy(() => import('../features/analytics/CancellationReportsPage'));
const CouponUsageReportPage = lazy(() => import('../features/analytics/CouponUsageReportPage'));
const CookieConsentReportPage = lazy(() => import('../features/analytics/CookieConsentReportPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const CronHistoryPage = lazy(() => import('../features/settings/CronHistoryPage'));
const CancellationSettingsPage = lazy(() => import('../features/settings/CancellationSettingsPage'));
const EmailSettingsPage = lazy(() => import('../features/settings/EmailSettingsPage'));
const NotificationCenterPage = lazy(() => import('../features/notifications/NotificationCenterPage'));
const MaintenanceModePage = lazy(() => import('../features/settings/MaintenanceModePage'));
const AdminIpAllowlistPage = lazy(() => import('../features/settings/AdminIpAllowlistPage'));
const AttackProtectionSettingsPage = lazy(() => import('../features/settings/AttackProtectionSettingsPage'));
const ActiveSessionsPage = lazy(() => import('../features/settings/ActiveSessionsPage'));
const DatabaseStatusPage = lazy(() => import('../features/settings/DatabaseStatusPage'));
const MobileAppSettingsPage = lazy(() => import('../features/settings/MobileAppSettingsPage'));
const WebsiteDesignSettingsPage = lazy(() => import('../features/settings/WebsiteDesignSettingsPage'));
const WebsiteSchemaSettingsPage = lazy(() => import('../features/settings/WebsiteSchemaSettingsPage'));
const DriverAppSettingsPage = lazy(() => import('../features/settings/DriverAppSettingsPage'));
const StatusManagementPage = lazy(() => import('../features/status/StatusManagementPage'));
const ProfilePage = lazy(() => import('../features/profile/ProfilePage'));
const ChangePasswordPage = lazy(() => import('../features/profile/ChangePasswordPage'));
const AccessControlPage = lazy(() => import('../features/security/AccessControlPage'));
const AdminAccessLogPage = lazy(() => import('../features/security/AdminAccessLogPage'));
const LogsPage = lazy(() => import('../features/logs/LogsPage'));
const QaTestResultsPage = lazy(() => import('../features/qa/QaTestResultsPage'));
const SupportTicketsPage = lazy(() => import('../features/support/SupportTicketsPage'));
const CrmCallLogsPage = lazy(() => import('../features/crm/CrmCallLogsPage'));
const CrmComplaintsPage = lazy(() => import('../features/crm/CrmComplaintsPage'));
const CrmRefundTicketsPage = lazy(() => import('../features/crm/CrmRefundTicketsPage'));
const CrmEscalationMatrixPage = lazy(() => import('../features/crm/CrmEscalationMatrixPage'));
const CrmWhatsappPage = lazy(() => import('../features/crm/CrmWhatsappPage'));
const BroadcastsPage = lazy(() => import('../features/broadcasts/BroadcastsPage'));
const VendorNotificationsPage = lazy(() => import('../features/vendor-notifications/VendorNotificationsPage'));
const VendorPenaltiesPage = lazy(() => import('../features/vendors/VendorPenaltiesPage'));
const CheckoutAddonsPage = lazy(() => import('../features/checkout/CheckoutAddonsPage'));
const CheckoutAddonsReportPage = lazy(() => import('../features/checkout/CheckoutAddonsReportPage'));
const SnackBoxManagementPage = lazy(() => import('../features/snack-boxes/SnackBoxManagementPage'));
const SnackBoxDashboardPage = lazy(() => import('../features/snack-boxes/SnackBoxDashboardPage'));
const SnackBoxInventoryPage = lazy(() => import('../features/snack-boxes/SnackBoxInventoryPage'));
const SnackBoxOrdersPage = lazy(() => import('../features/snack-boxes/SnackBoxOrdersPage'));
const SnackBoxReportsPage = lazy(() => import('../features/snack-boxes/SnackBoxReportsPage'));
const SnackBoxSettingsPage = lazy(() => import('../features/snack-boxes/SnackBoxSettingsPage'));
const ReviewDashboardPage = lazy(() => import('../features/reviews/ReviewDashboardPage'));
const ReviewRequestsPage = lazy(() => import('../features/reviews/ReviewRequestsPage'));
const ReviewRequestDetailPage = lazy(() => import('../features/reviews/ReviewRequestDetailPage'));
const ReviewReportsPage = lazy(() => import('../features/reviews/ReviewReportsPage'));
const ReviewSettingsPage = lazy(() => import('../features/reviews/ReviewSettingsPage'));
const CheckoutOnlinePaymentOptionsPage = lazy(
  () => import('../features/checkout/CheckoutOnlinePaymentOptionsPage')
);
const CheckoutTermsConditionsPage = lazy(
  () => import('../features/checkout/CheckoutTermsConditionsPage')
);
const AIRouteManager = lazy(() => import('../pages/RouteManager'));
const FinanceReportPage = lazy(() => import('../features/finance/FinanceReportPage'));
const FinanceDashboardPage = lazy(() => import('../features/finance/FinanceDashboardPage'));
const VehicleLedgerPage = lazy(() => import('../features/finance/VehicleLedgerPage'));
const FleetExpensesPage = lazy(() => import('../features/finance/FleetExpensesPage'));
const DriverSettlementLedgerPage = lazy(() => import('../features/finance/DriverSettlementLedgerPage'));
const VendorFinancePage = lazy(() => import('../features/finance/VendorFinancePage'));
const FastagImportPage = lazy(() => import('../features/finance/FastagImportPage'));
const ZohoExportPage = lazy(() => import('../features/finance/ZohoExportPage'));
const MarketingLeadsPage = lazy(() => import('../features/marketing/MarketingLeadsPage'));
const UtmTrackingDashboardPage = lazy(
  () => import('../features/utm-tracking/UtmTrackingDashboardPage'),
);
const UtmTrackingUrlsPage = lazy(() => import('../features/utm-tracking/UtmTrackingUrlsPage'));
const UtmTrackingDetailPage = lazy(() => import('../features/utm-tracking/UtmTrackingDetailPage'));
const NewsletterSubscribersPage = lazy(
  () => import('../features/newsletter/NewsletterSubscribersPage'),
);
const NewsletterCampaignsPage = lazy(
  () => import('../features/newsletter/NewsletterCampaignsPage'),
);
const NewsletterSettingsPage = lazy(
  () => import('../features/newsletter/NewsletterSettingsPage'),
);
const MarketingCampaignPage = lazy(() => import('../features/marketing/MarketingCampaignPage'));
const MarketingSourcePage = lazy(() => import('../features/marketing/MarketingSourcePage'));
const MarketingConversionPage = lazy(() => import('../features/marketing/MarketingConversionPage'));
const MarketingRevenuePage = lazy(() => import('../features/marketing/MarketingRevenuePage'));
const MarketingJourneyPage = lazy(() => import('../features/marketing/MarketingJourneyPage'));
const GoogleAdsDashboardPage = lazy(() => import('../features/google-ads/GoogleAdsDashboardPage'));
const GoogleAdsCampaignsPage = lazy(() => import('../features/google-ads/GoogleAdsCampaignsPage'));
const GoogleAdsKeywordsPage = lazy(() => import('../features/google-ads/GoogleAdsKeywordsPage'));
const GoogleAdsLandingPagesPage = lazy(() => import('../features/google-ads/GoogleAdsLandingPagesPage'));
const GoogleAdsAttributionPage = lazy(() => import('../features/google-ads/GoogleAdsAttributionPage'));
const GoogleAdsSpendImportPage = lazy(() => import('../features/google-ads/GoogleAdsSpendImportPage'));
const GoogleAdsReportsPage = lazy(() => import('../features/google-ads/GoogleAdsReportsPage'));
const GoogleAdsIntegrationsPage = lazy(() => import('../features/google-ads/GoogleAdsIntegrationsPage'));
const AiVisibilityDashboardPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityDashboardPage })),
);
const AiVisibilityUrlsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityUrlsPage })),
);
const AiVisibilityCrawlersPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityCrawlersPage })),
);
const AiVisibilityKeywordsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityKeywordsPage })),
);
const AiVisibilityOpportunitiesPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityOpportunitiesPage })),
);
const AiVisibilityStructuredDataPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityStructuredDataPage })),
);
const AiVisibilityTestsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityTestsPage })),
);
const AiVisibilityReferralsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityReferralsPage })),
);
const AiVisibilityReportsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilityReportsPage })),
);
const AiVisibilitySettingsPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilitySettingsPage })),
);
const AiVisibilitySeoAuditPage = lazy(() =>
  import('../features/ai-visibility/AiVisibilityPages').then((m) => ({ default: m.AiVisibilitySeoAuditPage })),
);

function PageFallback() {
  return (
    <div className="flex flex-1 items-center justify-center py-24">
      <LoadingSpinner size="large" message="Loading page..." />
    </div>
  );
}

export default function AdminAppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route
          path="/admin/watchtower"
          element={
            <RequirePermission permission={PERMISSIONS.WATCHTOWER_VIEW} title="Watch Tower access required">
              <WatchtowerPage />
            </RequirePermission>
          }
        />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/support/tickets" element={<SupportTicketsPage />} />
        <Route path="/contact/messages" element={<ContactMessagesPage />} />
        <Route path="/contact/settings" element={<ContactFormSettingsPage />} />
        <Route path="/crm/call-logs" element={<CrmCallLogsPage />} />
        <Route path="/crm/complaints" element={<CrmComplaintsPage />} />
        <Route path="/crm/refund-tickets" element={<CrmRefundTicketsPage />} />
        <Route path="/crm/escalation" element={<CrmEscalationMatrixPage />} />
        <Route path="/crm/whatsapp" element={<CrmWhatsappPage />} />
        <Route path="/bookings/view/:bookingId" element={<BookingViewPage />} />
        <Route path="/bookings/view" element={<BookingViewPage />} />
        <Route path="/bookings/edit/:bookingId" element={<BookingEditPage />} />
        <Route path="/bookings/edit" element={<BookingEditPage />} />
        <Route path="/bookings/activity" element={<BookingActivityLogsPage />} />
        <Route path="/bookings/invoices" element={<BookingInvoicesPage />} />
        <Route path="/bookings/transactions" element={<BookingTransactionsPage />} />
        <Route path="/bookings/manual-broadcast" element={<ManualBroadcastPage />} />
        <Route path="/bookings/guest-vendor-interests" element={<GuestVendorInterestsPage />} />
        <Route path="/bookings/vendor-cab-availability" element={<VendorCabAvailabilityPage />} />
        <Route path="/bookings/leads" element={<LeadsPage />} />
        <Route path="/bookings/quotes/:source/:quoteId" element={<QuoteViewPage />} />
        <Route path="/bookings/quotes" element={<QuotesPage />} />
        <Route path="/broadcasts" element={<BroadcastsPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/join-us-enquiries" element={<VendorJoinUsEnquiriesPage />} />
        <Route path="/vendors/pan-india" element={<PanIndiaVendorsPage />} />
        <Route
          path="/dmc/cab-prospects"
          element={(
            <RequirePermission permission="dmc:view">
              <DmcCabProspectsPage />
            </RequirePermission>
          )}
        />
        <Route
          path="/dmc/package-prospects"
          element={(
            <RequirePermission permission="dmc:view">
              <DmcPackageProspectsPage />
            </RequirePermission>
          )}
        />
        <Route path="/vendors/onboarding-campaigns" element={<VendorOnboardingCampaignsPage />} />
        <Route path="/vendors/onboarding-form" element={<VendorOnboardingFormSettingsPage />} />
        <Route
          path="/vendors/notifications"
          element={
            <RequirePermission permission="vendor-notifications:view">
              <VendorNotificationsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/vendors/penalties"
          element={
            <RequirePermission permission="vendor-penalties:view">
              <VendorPenaltiesPage />
            </RequirePermission>
          }
        />
        <Route path="/finance" element={<FinanceDashboardPage />} />
        <Route path="/finance/vehicle-ledger" element={<VehicleLedgerPage />} />
        <Route path="/finance/expenses" element={<FleetExpensesPage />} />
        <Route path="/finance/fastag-import" element={<FastagImportPage />} />
        <Route path="/finance/zoho-export" element={<ZohoExportPage />} />
        <Route path="/finance/checkout-addons-report" element={<CheckoutAddonsReportPage />} />
        <Route
          path="/finance/snackbox-orders-report"
          element={<SnackBoxOrdersPage embeddedInReports />}
        />
        <Route path="/finance/driver-settlements" element={<DriverSettlementLedgerPage />} />
        <Route path="/finance/vendor-finance" element={<VendorFinancePage />} />
        <Route
          path="/finance/fleet-utilization"
          element={<FinanceReportPage menuId="finance-fleet-utilization" />}
        />
        <Route
          path="/finance/driver-performance"
          element={<FinanceReportPage menuId="finance-driver-performance" />}
        />
        <Route
          path="/finance/gst-summary"
          element={<FinanceReportPage menuId="finance-gst-summary" />}
        />
        <Route
          path="/finance/vendor-settlement"
          element={<FinanceReportPage menuId="finance-vendor-settlement" />}
        />
        <Route
          path="/finance/vehicle-pl"
          element={<FinanceReportPage menuId="finance-vehicle-pl" />}
        />
        <Route
          path="/finance/vendor-payouts"
          element={<FinanceReportPage menuId="finance-vendor-payouts" />}
        />
        <Route
          path="/finance/driver-settlements"
          element={<FinanceReportPage menuId="finance-driver-settlements" />}
        />
        <Route
          path="/finance/toll-fuel"
          element={<FinanceReportPage menuId="finance-toll-fuel" />}
        />
        <Route
          path="/finance/gst-invoices"
          element={<FinanceReportPage menuId="finance-gst-invoices" />}
        />
        <Route
          path="/finance/customer-payments"
          element={<FinanceReportPage menuId="finance-customer-payments" />}
        />
        <Route
          path="/finance/fleet-profitability"
          element={<FinanceReportPage menuId="finance-fleet-profitability" />}
        />
        <Route
          path="/finance/emi-loans"
          element={<FinanceReportPage menuId="finance-emi-loans" />}
        />
        <Route
          path="/finance/route-pl"
          element={<FinanceReportPage menuId="finance-route-pl" />}
        />
        <Route
          path="/finance/multi-vendor"
          element={<FinanceReportPage menuId="finance-multi-vendor" />}
        />
        <Route
          path="/finance/reconciliation"
          element={<FinanceReportPage menuId="finance-reconciliation" />}
        />
        <Route path="/cabs/fleet" element={<CabsManagementPage />} />
        <Route
          path="/cabs/approval"
          element={
            <RequirePermission permission="cabs-approval:view">
              <CabsApprovalPage />
            </RequirePermission>
          }
        />
        <Route path="/cabs/segments" element={<SegmentConfigurationPage />} />
        <Route path="/cabs/expenses" element={<CabExpensesPage />} />
        <Route path="/cabs/revenue" element={<CabWiseRevenuePage />} />
        <Route path="/cabs/sold-out" element={<CabSoldOutPage />} />
        <Route path="/drivers" element={<DriverManagementPage />} />
        <Route
          path="/drivers/approval"
          element={
            <RequirePermission permission="driver-approval:view">
              <DriverApprovalPage />
            </RequirePermission>
          }
        />
        <Route path="/drivers/salary-slips" element={<DriverSalarySlipPage />} />
        <Route path="/drivers/expenses" element={<DriverExpensesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/tours/cab-packages" element={<CabPackagesPage />} />
        <Route path="/tours/enquiries" element={<CabPackageEnquiriesPage />} />
        <Route path="/tours/user-logged" element={<CabPackageUserLoggedPage />} />
        <Route
          path="/route-pages/user-logged"
          element={
            <RequirePermission permission="route-pages:view">
              <RoutePageUserLoggedPage />
            </RequirePermission>
          }
        />
        <Route
          path="/vehicle-landings/user-logged"
          element={
            <RequirePermission permission="routes:view">
              <VehicleLandingUserLoggedPage />
            </RequirePermission>
          }
        />
        <Route path="/tours/nearby-places" element={<NearbyPlacesPage />} />
        <Route path="/tours/jyotirlinga" element={<JyotirlingaEnginePage />} />
        <Route path="/tours/jyotirlinga/sites" element={<PilgrimageSitesPage />} />
        <Route
          path="/tours/ashtavinayak"
          element={
            <RequirePermission permission="ashtavinayak-cms:view">
              <AshtavinayakToursPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tours/ashtavinayak/temples"
          element={
            <RequirePermission permission="ashtavinayak-cms:view">
              <AshtavinayakTemplesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tours/ashtavinayak/:id"
          element={
            <RequirePermission permission="ashtavinayak-cms:view">
              <AshtavinayakTourEditPage />
            </RequirePermission>
          }
        />
        <Route
          path="/luxury-cars"
          element={
            <RequirePermission permission="luxury-cars:view">
              <LuxuryCarsDashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/luxury-cars/cities"
          element={
            <RequirePermission permission="luxury-cars:view">
              <LuxuryCitiesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/luxury-cars/fleet"
          element={
            <RequirePermission permission="luxury-cars:view">
              <LuxuryCarsFleetPage />
            </RequirePermission>
          }
        />
        <Route path="/luxury-cars/tariffs" element={<RequirePermission permission="luxury-cars:view"><LuxuryTariffsPage /></RequirePermission>} />
        <Route path="/luxury-cars/packages" element={<RequirePermission permission="luxury-cars:view"><LuxuryPackagesPage /></RequirePermission>} />
        <Route path="/luxury-cars/pages" element={<RequirePermission permission="luxury-cars:view"><LuxuryPagesPage /></RequirePermission>} />
        <Route path="/luxury-cars/faqs" element={<RequirePermission permission="luxury-cars:view"><LuxuryFaqsPage /></RequirePermission>} />
        <Route path="/luxury-cars/faq-categories" element={<RequirePermission permission="luxury-cars:view"><LuxuryFaqCategoriesPage /></RequirePermission>} />
        <Route path="/luxury-cars/faq-import" element={<RequirePermission permission="luxury-cars:manage"><LuxuryFaqImportPage /></RequirePermission>} />
        <Route path="/luxury-cars/faq-templates" element={<RequirePermission permission="luxury-cars:view"><LuxuryFaqTemplatesPage /></RequirePermission>} />
        <Route path="/luxury-cars/faq-analytics" element={<RequirePermission permission="luxury-cars:view"><LuxuryFaqAnalyticsPage /></RequirePermission>} />
        <Route path="/luxury-cars/enquiries" element={<RequirePermission permission="luxury-cars:view"><LuxuryEnquiriesPage /></RequirePermission>} />
        <Route path="/luxury-cars/bookings" element={<RequirePermission permission="luxury-cars:view"><LuxuryBookingsPage /></RequirePermission>} />
        <Route path="/luxury-cars/vendors" element={<RequirePermission permission="luxury-cars:view"><LuxuryVendorsPage /></RequirePermission>} />
        <Route path="/luxury-cars/vendors/new" element={<RequirePermission permission="luxury-cars:view"><LuxuryVendorEditPage /></RequirePermission>} />
        <Route path="/luxury-cars/vendors/:id/edit" element={<RequirePermission permission="luxury-cars:view"><LuxuryVendorEditPage /></RequirePermission>} />
        <Route path="/luxury-cars/pricing" element={<RequirePermission permission="luxury-cars:view"><LuxuryPricingMatrixPage /></RequirePermission>} />
        <Route path="/luxury-cars/settings" element={<RequirePermission permission="luxury-cars:view"><LuxurySettingsPage /></RequirePermission>} />
        <Route path="/luxury-cars/terms" element={<RequirePermission permission="luxury-cars:view"><LuxuryTermsPage /></RequirePermission>} />
        <Route path="/luxury-cars/welcome-goodies" element={<RequirePermission permission="luxury-cars:view"><LuxuryWelcomeGoodiesPage /></RequirePermission>} />
        <Route
          path="/luxury-cars/activities"
          element={
            <RequirePermission permission="luxury-cars:view">
              <LuxuryCarsActivitiesPage />
            </RequirePermission>
          }
        />
        <Route path="/tours/settings" element={<ToursPackagesSettingsPage />} />
        <Route path="/pricing/cab-packages" element={<Navigate to="/tours/cab-packages" replace />} />
        <Route path="/pricing/vendor" element={<VendorPricingPage />} />
        <Route path="/pricing/price-analysis" element={<PriceAnalysisPage />} />
        <Route path="/pricing/price-analysis/history" element={<PriceAnalysisHistoryPage />} />
        <Route path="/pricing/analysis" element={<Navigate to="/pricing/price-analysis" replace />} />
        <Route path="/pricing/configuration" element={<PricingConfigurationPage />} />
        <Route path="/pricing/test" element={<PricingTestPage />} />
        <Route path="/cities" element={<CityManagementPage />} />
        <Route path="/cities/missing-searches" element={<LocationMissingSearchesPage />} />
        <Route path="/cities/search-index" element={<LocationSearchIndexPage />} />
        <Route path="/cities/master-ops" element={<LocationMasterOpsPage />} />
        <Route path="/routes" element={<TariffConfigPage />} />
        <Route path="/routes/edit/:id" element={<ConfigRouteEditPage />} />
        <Route path="/routes/settings" element={<RoutesSettingsPage />} />
        <Route path="/cities/distance" element={<DistanceCalculatorPage />} />
        <Route path="/route-pages" element={<RoutePagesManagement />} />
        <Route
          path="/seo"
          element={
            <RequirePermission permission="seo:view">
              <SeoControlCenterPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/setup-wizard"
          element={
            <RequirePermission permission="seo:view">
              <SeoSetupWizardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/analyzer"
          element={
            <RequirePermission permission="seo:view">
              <SeoAnalyzerPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/serp-preview"
          element={
            <RequirePermission permission="seo:view">
              <SeoSerpPreviewPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/schema"
          element={
            <RequirePermission permission="seo:view">
              <SeoSchemaPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/rank-tracker"
          element={
            <RequirePermission permission="seo:view">
              <SeoRankTrackerPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/404-monitor"
          element={
            <RequirePermission permission="seo:view">
              <Seo404MonitorPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/content"
          element={
            <RequirePermission permission="seo:view">
              <SeoContentOverviewPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/internal-links"
          element={
            <RequirePermission permission="seo:view">
              <SeoInternalLinksPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/page-urls"
          element={
            <RequirePermission permission="seo:view">
              <SeoPageUrlsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/score-dashboard"
          element={
            <RequirePermission permission="seo:view">
              <SeoScoreDashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/locality-landings"
          element={
            <RequirePermission permission="seo:view">
              <LocalityLandingCmsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/city-money-pages"
          element={
            <RequirePermission permission="seo:view">
              <CityMoneyPagesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo-engine/*"
          element={
            <RequirePermission permission="seo:view">
              <SeoEngineNestedRoutes />
            </RequirePermission>
          }
        />
        <Route
          path="/seo-engine"
          element={
            <RequirePermission permission="seo:view">
              <SeoEngineNestedRoutes />
            </RequirePermission>
          }
        />
        <Route
          path="/ai-seo/*"
          element={
            <RequirePermission permission="seo:view">
              <AiSeoNestedRoutes />
            </RequirePermission>
          }
        />
        <Route
          path="/ai-seo"
          element={
            <RequirePermission permission="seo:view">
              <AiSeoNestedRoutes />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/image-seo"
          element={
            <RequirePermission permission="seo:view">
              <SeoImageSeoPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/local-seo"
          element={
            <RequirePermission permission="seo:view">
              <SeoLocalSeoPage />
            </RequirePermission>
          }
        />
        <Route
          path="/seo/index-status"
          element={
            <RequirePermission permission="seo:view">
              <SeoIndexStatusPage />
            </RequirePermission>
          }
        />
        <Route path="/marketing/sitemaps" element={<SitemapPage />} />
        <Route path="/marketing/robots-txt" element={<RobotsTxtPage />} />
        <Route path="/checkout/addons" element={<CheckoutAddonsPage />} />
        <Route path="/checkout/snack-box-dashboard" element={<SnackBoxDashboardPage />} />
        <Route path="/checkout/snack-boxes" element={<SnackBoxManagementPage />} />
        <Route path="/checkout/snack-box-inventory" element={<SnackBoxInventoryPage />} />
        <Route path="/checkout/snack-box-orders" element={<SnackBoxOrdersPage />} />
        <Route path="/checkout/snack-box-reports" element={<SnackBoxReportsPage />} />
        <Route path="/checkout/snack-box-settings" element={<SnackBoxSettingsPage />} />
        <Route path="/checkout/payment-options" element={<CheckoutOnlinePaymentOptionsPage />} />
        <Route path="/checkout/terms" element={<CheckoutTermsConditionsPage />} />
        <Route path="/content/cms-pages" element={<CmsRoutePagesManagement />} />
        <Route path="/content/cms-pages/edit" element={<CmsRoutePageEditor />} />
        <Route path="/content/cms-styles" element={<CmsRoutePageCssEditor />} />
        <Route path="/content/popular-routes" element={<HomepagePopularRoutesPage />} />
        <Route path="/content/route-categories" element={<HomepagePopularRouteCategoriesPage />} />
        <Route path="/homepage/header" element={<HomepageHeaderPage />} />
        <Route path="/homepage/hero-banner" element={<HomepageHeroBannerPage />} />
        <Route path="/homepage/footer" element={<HomepageFooterPage />} />
        <Route path="/homepage/not-found" element={<HomepageNotFoundPage />} />
        <Route path="/homepage/sections" element={<HomepageSectionsPage />} />
        <Route path="/homepage/gallery-share" element={<GalleryShareSettingsPage />} />
        <Route path="/content/faq-categories" element={<FaqCategoriesPage />} />
        <Route path="/content/faqs" element={<FaqsPage />} />
        <Route path="/content/blog-posts" element={<BlogPostsManagement />} />
        <Route path="/content/blog-posts/edit" element={<BlogPostEditor />} />
        <Route path="/content/blog-dashboard" element={<BlogSeoDashboard />} />
        <Route path="/content/blog-calendar" element={<BlogCalendarPage />} />
        <Route path="/content/blog-keywords" element={<BlogKeywordsPage />} />
        <Route path="/content/blog-clusters" element={<BlogClustersPage />} />
        <Route path="/content/blog-authors" element={<BlogAuthorsPage />} />
        <Route path="/content/blog-internal-links" element={<BlogInternalLinksPage />} />
        <Route path="/content/blog-seo-audit" element={<BlogSeoAuditPage />} />
        <Route path="/content/blog-cannibalization" element={<BlogCannibalizationPage />} />
        <Route path="/content/blog-redirects" element={<BlogRedirectsPage />} />
        <Route path="/content/blog-categories" element={<BlogCategoriesPage />} />
        <Route path="/content/blog-tags" element={<BlogTagsPage />} />
        <Route path="/content/blog-comments" element={<BlogCommentsPage />} />
        <Route path="/content/reviews" element={<ReviewsPage />} />
        <Route path="/reviews/dashboard" element={<ReviewDashboardPage />} />
        <Route path="/reviews/requests" element={<ReviewRequestsPage />} />
        <Route path="/reviews/requests/:id" element={<ReviewRequestDetailPage />} />
        <Route path="/reviews/reports" element={<ReviewReportsPage />} />
        <Route
          path="/reviews/settings"
          element={
            <RequirePermission permission="reviews:manage" title="Review settings access required">
              <ReviewSettingsPage />
            </RequirePermission>
          }
        />
        <Route path="/content/ai-routes" element={<AIRouteManager />} />
        <Route path="/customers" element={<UsersPage />} />
        <Route path="/customers/activities" element={<CustomerActivitiesLogPage />} />
        <Route path="/corporate/clients" element={<CorporateClientsPage />} />
        <Route path="/corporate/pending" element={<CorporatePendingApprovalsPage />} />
        <Route path="/corporate/bookings" element={<CorporateBookingsPage />} />
        <Route path="/corporate/pricing" element={<CorporatePricingPage />} />
        <Route path="/corporate/invoices" element={<CorporateInvoicesAdminPage />} />
        <Route path="/corporate/reports" element={<CorporateReportsPage />} />
        <Route path="/corporate/contracts" element={<CorporateContractsPage />} />
        <Route path="/corporate/erp" element={<CorporateErpPage />} />
        <Route path="/corporate/clients/:clientId" element={<CorporateClientDetailPage />} />
        <Route path="/corporate-cms/hub" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsHubPage /></RequirePermission>} />
        <Route path="/corporate-cms/cities" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsCitiesPage /></RequirePermission>} />
        <Route path="/corporate-cms/services" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsServicesPage /></RequirePermission>} />
        <Route path="/corporate-cms/packages" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsPackagesPage /></RequirePermission>} />
        <Route path="/corporate-cms/enquiries" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsEnquiriesPage /></RequirePermission>} />
        <Route path="/corporate-cms/localities" element={<RequirePermission permission="corporate-cms:view"><CorporateCmsLocalitiesPage /></RequirePermission>} />
        <Route path="/customers/view/:customerId" element={<CustomerViewPage />} />
        <Route path="/customers/edit/:customerId" element={<CustomerEditPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/advanced" element={<AdvancedAnalyticsPage />} />
        <Route path="/analytics/booking-funnel" element={<BookingFunnelPage />} />
        <Route path="/analytics/visitors" element={<VisitorsReportPage />} />
        <Route path="/analytics/cancellations" element={<CancellationReportsPage />} />
        <Route path="/analytics/coupon-usage" element={<CouponUsageReportPage />} />
        <Route path="/analytics/cookie-consent" element={<CookieConsentReportPage />} />
        <Route path="/marketing/leads" element={<MarketingLeadsPage />} />
        <Route path="/marketing-analytics/dashboard" element={<RequirePermission permission="google_ads:view"><GoogleAdsDashboardPage /></RequirePermission>} />
        <Route path="/marketing-analytics/campaigns" element={<RequirePermission permission="google_ads:view"><GoogleAdsCampaignsPage /></RequirePermission>} />
        <Route path="/marketing-analytics/performance" element={<RequirePermission permission="google_ads:view"><GoogleAdsDashboardPage /></RequirePermission>} />
        <Route path="/marketing-analytics/keywords" element={<RequirePermission permission="google_ads:view"><GoogleAdsKeywordsPage /></RequirePermission>} />
        <Route path="/marketing-analytics/landing-pages" element={<RequirePermission permission="google_ads:view"><GoogleAdsLandingPagesPage /></RequirePermission>} />
        <Route path="/marketing-analytics/attribution" element={<RequirePermission permission="google_ads:view"><GoogleAdsAttributionPage /></RequirePermission>} />
        <Route path="/marketing-analytics/conversion-tracking" element={<RequirePermission permission="google_ads:view"><GoogleAdsIntegrationsPage /></RequirePermission>} />
        <Route path="/marketing-analytics/spend-import" element={<RequirePermission permission="google_ads:manage"><GoogleAdsSpendImportPage /></RequirePermission>} />
        <Route path="/marketing-analytics/reports" element={<RequirePermission permission="google_ads:export"><GoogleAdsReportsPage /></RequirePermission>} />
        <Route path="/marketing-analytics/integrations" element={<RequirePermission permission="google_ads:view"><GoogleAdsIntegrationsPage /></RequirePermission>} />
        <Route path="/marketing-analytics/settings" element={<RequirePermission permission="google_ads:view"><GoogleAdsIntegrationsPage /></RequirePermission>} />
        <Route path="/ai-visibility/dashboard" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityDashboardPage /></RequirePermission>} />
        <Route path="/ai-visibility/urls" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityUrlsPage /></RequirePermission>} />
        <Route path="/ai-visibility/crawlers" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityCrawlersPage /></RequirePermission>} />
        <Route path="/ai-visibility/keywords" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityKeywordsPage /></RequirePermission>} />
        <Route path="/ai-visibility/opportunities" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityOpportunitiesPage /></RequirePermission>} />
        <Route path="/ai-visibility/structured-data" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityStructuredDataPage /></RequirePermission>} />
        <Route path="/ai-visibility/tests" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityTestsPage /></RequirePermission>} />
        <Route path="/ai-visibility/referrals" element={<RequirePermission permission="ai_visibility:view"><AiVisibilityReferralsPage /></RequirePermission>} />
        <Route path="/ai-visibility/reports" element={<RequirePermission permission="ai_visibility:export"><AiVisibilityReportsPage /></RequirePermission>} />
        <Route path="/ai-visibility/seo-audit" element={<RequirePermission permission="ai_visibility:view"><AiVisibilitySeoAuditPage /></RequirePermission>} />
        <Route path="/ai-visibility/settings" element={<RequirePermission permission="ai_visibility:view"><AiVisibilitySettingsPage /></RequirePermission>} />
        <Route
          path="/utm-tracking"
          element={
            <RequirePermission permission="utm_tracking:view">
              <UtmTrackingDashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/utm-tracking/urls"
          element={
            <RequirePermission permission="utm_tracking:view">
              <UtmTrackingUrlsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/utm-tracking/urls/:id"
          element={
            <RequirePermission permission="utm_tracking:view">
              <UtmTrackingDetailPage />
            </RequirePermission>
          }
        />
        <Route path="/marketing/newsletter" element={<NewsletterSubscribersPage />} />
        <Route path="/marketing/newsletter/campaigns" element={<NewsletterCampaignsPage />} />
        <Route path="/marketing/newsletter/settings" element={<NewsletterSettingsPage />} />
        <Route path="/marketing/business-presence" element={<BusinessPresenceDashboard />} />
        <Route path="/marketing/business-presence/locations" element={<BusinessLocationsPage />} />
        <Route path="/marketing/business-presence/posts" element={<BusinessPostsPage />} />
        <Route path="/marketing/business-presence/reviews" element={<BusinessReviewsInboxPage />} />
        <Route path="/marketing/business-presence/settings" element={<BusinessPresenceSettingsPage />} />
        <Route path="/marketing/business-presence/accounts" element={<BusinessSocialAccountsPage />} />
        <Route path="/marketing/business-presence/seo" element={<BusinessSeoPagesPage />} />
        <Route
          path="/marketing/business-presence/oauth/callback/:provider"
          element={<BusinessPresenceOAuthCallbackPage />}
        />
        <Route path="/marketing/campaigns" element={<MarketingCampaignPage />} />
        <Route path="/marketing/sources" element={<MarketingSourcePage />} />
        <Route path="/marketing/conversion" element={<MarketingConversionPage />} />
        <Route path="/marketing/revenue" element={<MarketingRevenuePage />} />
        <Route path="/marketing/journey/:leadId" element={<MarketingJourneyPage />} />
        <Route path="/ads" element={<AdsDashboardPage />} />
        <Route path="/ads/campaigns" element={<AdsCampaignsListPage />} />
        <Route path="/ads/campaigns/new" element={<AdsCampaignEditorPage />} />
        <Route path="/ads/campaigns/:id" element={<AdsCampaignEditorPage />} />
        <Route path="/ads/advertisers" element={<AdsAdvertisersPage />} />
        <Route path="/ads/placements" element={<AdsPlacementsPage />} />
        <Route path="/ads/landing-pages" element={<AdsLandingPagesPage />} />
        <Route path="/ads/reports" element={<AdsReportsPage />} />
        <Route path="/ads/settings" element={<AdsSettingsPage />} />
        <Route path="/logs" element={<LogsPage />} />
        <Route path="/qa-test-results" element={<QaTestResultsPage />} />
        <Route path="/tools/code-flow" element={<CodeFlowPage />} />
        <Route path="/tools/dev-doc" element={<DevDocPage />} />
        <Route
          path="/tools/db-backup"
          element={
            <RequirePermission permission="database:view" title="Database access required">
              <DbBackupPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tools/cache-management"
          element={
            <RequirePermission permission="settings:view" title="Settings access required">
              <CacheManagementPage />
            </RequirePermission>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/cron-history" element={<CronHistoryPage />} />
        <Route path="/settings/loyalty" element={<SettingsPage initialTab="loyalty" />} />
        <Route path="/settings/chatbot" element={<SettingsPage initialTab="chatbot" />} />
        <Route path="/settings/miscellaneous" element={<SettingsPage initialTab="miscellaneous" />} />
        <Route path="/settings/cancellation" element={<CancellationSettingsPage />} />
        <Route path="/settings/email" element={<EmailSettingsPage />} />
        <Route path="/settings/notifications" element={<NotificationCenterPage />} />
        <Route path="/settings/maintenance" element={<MaintenanceModePage />} />
        <Route path="/settings/admin-ip-allowlist" element={<AdminIpAllowlistPage />} />
        <Route path="/settings/attack-protection" element={<AttackProtectionSettingsPage />} />
        <Route path="/settings/active-sessions" element={<ActiveSessionsPage />} />
        <Route
          path="/settings/database"
          element={
            <RequirePermission permission="database:view" title="Database access required">
              <DatabaseStatusPage />
            </RequirePermission>
          }
        />
        <Route path="/settings/mobile-app" element={<MobileAppSettingsPage />} />
        <Route path="/settings/website-design" element={<WebsiteDesignSettingsPage />} />
        <Route path="/settings/website-schema" element={<WebsiteSchemaSettingsPage />} />
        <Route path="/settings/driver-app" element={<DriverAppSettingsPage />} />
        <Route path="/settings/statuses" element={<StatusManagementPage />} />
        <Route path="/account/profile" element={<ProfilePage />} />
        <Route path="/account/password" element={<ChangePasswordPage />} />
        <Route path="/access/users" element={<AccessControlPage initialSection="all-users" />} />
        <Route path="/access/locked" element={<AccessControlPage initialSection="locked-users" />} />
        <Route path="/access/roles" element={<AccessControlPage initialSection="user-roles" />} />
        <Route path="/access/logs" element={<AdminAccessLogPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
